import { AssigneeSelectComponent, AssigneeChange } from '../../company/assignee-select/assignee-select';
import { CompanyContextService } from '../../../services/company-context';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CargoRequest, CargoRequestProduct, CargoRequestService } from '../../../services/api/cargo-request';
import { DictionaryService, CountryDto } from '../../../services/api/dictionary';
import { FreightForwarderBidService, FreightForwarderBidDto } from '../../../services/api/freight-forwarder-bid';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { Subscription } from 'rxjs';
import { ChatComponent } from '../../shared/chat/chat.component';
import { ConfirmDialogService } from '../../../services/confirm-dialog';
import { ChatService, ChatMessage } from '../../../services/api/chat';
import { ChatSocketService } from '../../../services/api/chat-socket';
import { formatRequestNumber, DocNumberPipe } from '../../../services/request-number';
import { lockPageScroll, unlockPageScroll } from '../../../services/scroll-lock';

/** Последнее прочитанное сообщение чата по запросу — хранится в браузере, пока на сервере нет счётчика непрочитанных. */
const CHAT_SEEN_KEY = 'bilload.chat.lastSeen';

@Component({
  selector: 'app-freight-forwarder-view-request',
  standalone: true,
  imports: [CommonModule, RouterModule, AssigneeSelectComponent, ChatComponent, DocNumberPipe],
  templateUrl: './view-request.html',
  styleUrl: './view-request.scss'
})
export class FreightForwarderViewRequestComponent implements OnInit, OnDestroy {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);
  private chatService = inject(ChatService);
  private chatSocket = inject(ChatSocketService);

  requestId: number | null = null;
  /** Сам запрос — страница грузит его сама (раньше это делал общий блок, теперь у экспедитора своя вёрстка). */
  request: CargoRequest | null = null;
  loadingRequest = false;
  errorRequest: string | null = null;
  countries: CountryDto[] = [];
  cargoTypes: any[] = [];
  currencies: any[] = [];
  /** По заявке уже заключена сделка (в т.ч. не обязательно с этим экспедитором) — чат заявки блокируется. */
  baseRequestHasDeal = false;
  myBid: FreightForwarderBidDto | null = null;
  loadingBid = false;
  errorBid: string | null = null;
  deletingBid = false;
  returnTo: string | null = null; // Откуда пришли: 'dashboard' или 'requests'

  // Translations
  titleText = 'Просмотр запроса';
  backText = 'Назад к списку';
  chatButtonText = 'Чат';
  myBidSectionTitleText = 'Моя ставка';
  noBidText = 'Вы еще не сделали ставку на этот запрос';
  makeBidText = 'Сделать ставку';
  editBidText = 'Редактировать ставку';
  loadingBidText = 'Загрузка ставки...';
  priceText = 'Стоимость перевозки';
  currencyText = 'Валюта';
  deliveryDaysText = 'Срок доставки';
  servicesText = 'Услуги';
  noteText = 'Примечание';
  daysText = 'дней';
  createdAtText = 'Дата создания';
  statusText = 'Статус';
  acceptedText = 'Принята';
  pendingText = 'Ожидает';
  bidAcceptedInfoText = 'Ставка принята. Редактирование и удаление невозможны.';
  bidLostInfoText = 'Ставка проиграла. Редактирование и удаление невозможны.';
  deleteBidText = 'Удалить ставку';
  deletingBidText = 'Удаление...';
  deleteBidConfirmText = 'Вы уверены, что хотите удалить эту ставку?';
  deleteBidSuccessText = 'Ставка успешно удалена';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private bidService: FreightForwarderBidService,
    private cargoRequestService: CargoRequestService,
    private dictionaryService: DictionaryService,
    private languageService: LanguageService,
    private authService: AuthService,
    public companyContext: CompanyContextService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.companyContext.ensureLoaded();
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('FreightForwarderViewRequest: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('FreightForwarderViewRequest: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_FREIGHT_FORWARDER') {
        console.warn('FreightForwarderViewRequest: User is not freight forwarder, redirecting to home');
        this.router.navigate(['/']);
        return;
      }

      const id = this.route.snapshot.paramMap.get('id');
      // Получаем параметр returnTo из query string
      this.returnTo = this.route.snapshot.queryParams['returnTo'] || null;
      
      if (id) {
        this.requestId = +id;
        this.loadRequest(+id);
        this.loadDictionaries();
        this.loadMyBid(+id);
        this.watchChat(+id);
        this.updateTranslations();

        this.subscriptions.add(
          this.languageService.getTranslations().subscribe(() => {
            this.updateTranslations();
          })
        );
      }
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.chatCloseTimer) clearTimeout(this.chatCloseTimer);
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    if (this.chatOpen) this.unlockScroll();
    this.subscriptions.unsubscribe();
  }

  /** Чат с клиентом — выезжающая панель; страница под ней не прокручивается. */
  chatOpen = false;

  /** Переход из уведомления (?chat=1) — сразу открываем окно чата и убираем параметр из адреса. */
  private chatLinkWatched = false;

  private openChatFromLink(): void {
    if (!this.chatLinkWatched) {
      this.chatLinkWatched = true;
      this.subscriptions.add(this.route.queryParamMap.subscribe(() => this.openChatFromLink()));
      return;
    }
    if (this.route.snapshot.queryParamMap.get('chat') !== '1' || !this.request) return;
    const btn = document.querySelector('.chat-btn') as HTMLElement | null;
    if (!this.chatOpen) this.openChat(btn ?? undefined);
    this.router.navigate([], { relativeTo: this.route, queryParams: { chat: null }, queryParamsHandling: 'merge', replaceUrl: true });
    this.cdr.markForCheck();
  }

  /** Новые сообщения клиента с последнего открытия чата — показываются на кнопке. */
  unreadCount = 0;
  private lastSeenId = 0;
  private latestId = 0;
  private unreadIds = new Set<number>();

  private watchChat(id: number): void {
    const me = this.authService.getUser()?.id ?? undefined;
    this.lastSeenId = this.readSeen(id);
    const track = (m: ChatMessage) => {
      if (m.id > this.latestId) this.latestId = m.id;
      if (this.chatOpen) { this.markSeen(); return; }
      if (m.sender?.id !== me && !m.deleted && m.id > this.lastSeenId) this.unreadIds.add(m.id);
      this.unreadCount = this.unreadIds.size;
    };
    this.subscriptions.add(
      this.chatService.getMessages('REQUEST', id, 0, 50, me).subscribe({
        next: res => { res.content.forEach(track); this.cdr.markForCheck(); },
        error: () => {}
      })
    );
    this.subscriptions.add(
      this.chatSocket.watch('REQUEST', id, me).subscribe({
        next: ev => {
          if (ev.type === 'CREATED') track(ev.message);
          else if (ev.type === 'DELETED') { this.unreadIds.delete(ev.message.id); this.unreadCount = this.unreadIds.size; }
          this.cdr.markForCheck();
        },
        error: () => {}
      })
    );
  }

  private readSeen(id: number): number {
    try { return JSON.parse(localStorage.getItem(CHAT_SEEN_KEY) || '{}')[id] || 0; } catch { return 0; }
  }

  private markSeen(): void {
    this.unreadIds.clear();
    this.unreadCount = 0;
    if (!this.requestId || this.latestId <= this.lastSeenId) return;
    this.lastSeenId = this.latestId;
    try {
      const map = JSON.parse(localStorage.getItem(CHAT_SEEN_KEY) || '{}');
      map[this.requestId] = this.latestId;
      localStorage.setItem(CHAT_SEEN_KEY, JSON.stringify(map));
    } catch { /* приватный режим — счётчик просто не сохранится */ }
  }

  /** Окно чата «вылетает» из кнопки и сворачивается обратно в неё: позиция и точка анимации считаются от кнопки. */
  chatClosing = false;
  chatBox: { top: number; left: number; width: number; height: number; origin: string } | null = null;
  private chatAnchor: HTMLElement | null = null;
  private chatCloseTimer: ReturnType<typeof setTimeout> | null = null;

  openChat(anchor?: HTMLElement): void {
    if (this.chatCloseTimer) { clearTimeout(this.chatCloseTimer); this.chatCloseTimer = null; }
    if (anchor) this.chatAnchor = anchor;
    this.chatBox = this.measureChatBox();
    this.chatClosing = false;
    this.chatOpen = true;
    this.markSeen();
    this.lockScroll();
  }

  closeChat(): void {
    if (!this.chatOpen || this.chatClosing) return;
    this.markSeen();
    this.chatBox = this.measureChatBox() ?? this.chatBox;
    this.chatClosing = true;
    this.chatCloseTimer = setTimeout(() => {
      this.chatOpen = false;
      this.chatClosing = false;
      this.chatCloseTimer = null;
      this.unlockScroll();
      this.cdr.markForCheck();
    }, 200);
  }

  /** Блокируем прокрутку страницы под окном чата, не давая ей сдвинуться: место полосы прокрутки занимает отступ. */
  private lockScroll(): void {
    lockPageScroll();
  }

  private unlockScroll(): void {
    unlockPageScroll();
  }

  private measureChatBox(): { top: number; left: number; width: number; height: number; origin: string } | null {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect = this.chatAnchor?.getBoundingClientRect();
    // Телефон — на весь экран
    if (vw < 640) {
      const ox = rect ? rect.left + rect.width / 2 : vw / 2;
      const oy = rect ? rect.top + rect.height / 2 : vh / 2;
      return { top: 0, left: 0, width: vw, height: vh, origin: `${ox}px ${oy}px` };
    }
    const width = Math.min(420, vw - 32);
    const height = Math.min(640, vh - 32);
    if (!rect) return { top: vh - 16 - height, left: vw - 16 - width, width, height, origin: '100% 100%' };
    const left = Math.max(16, Math.min(rect.right - width, vw - 16 - width));
    let top = rect.bottom + 10;
    if (top + height > vh - 16) top = Math.max(16, vh - 16 - height);
    const ox = rect.left + rect.width / 2 - left;
    const oy = rect.top + rect.height / 2 - top;
    return { top, left, width, height, origin: `${ox}px ${oy}px` };
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.chatOpen && !this.chatClosing) this.chatBox = this.measureChatBox();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.chatOpen) this.closeChat();
  }

  private loadRequest(id: number): void {
    this.loadingRequest = true;
    this.errorRequest = null;
    this.subscriptions.add(
      this.cargoRequestService.getCargoRequest(id).subscribe({
        next: request => {
          this.request = request;
          setTimeout(() => this.openChatFromLink());
          this.baseRequestHasDeal = !!request.hasDeal;
          this.loadingRequest = false;
          this.cdr.markForCheck();
        },
        error: err => {
          this.errorRequest = err.status === 404
            ? this.t('ffRequest.notFound', 'Запрос не найден')
            : this.t('ffRequest.loadError', 'Не удалось загрузить запрос. Обновите страницу.');
          this.loadingRequest = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  private loadDictionaries(): void {
    this.subscriptions.add(this.dictionaryService.getAllCountries().subscribe({ next: c => { this.countries = c; this.cdr.markForCheck(); }, error: () => {} }));
    this.subscriptions.add(this.dictionaryService.getCargoTypes().subscribe({ next: c => { this.cargoTypes = c; this.cdr.markForCheck(); }, error: () => {} }));
    this.subscriptions.add(this.dictionaryService.getCurrencies().subscribe({ next: c => { this.currencies = c; this.cdr.markForCheck(); }, error: () => {} }));
  }

  // ─── Отображение ──────────────────────────────────────────────────────────

  /** Номер запроса для людей: BL-26-84. */
  requestNo(r: { id: number; createdAt?: string }): string {
    return formatRequestNumber(r.id, r.createdAt);
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  private get numberLocale(): string {
    const lang = this.languageService.getCurrentLanguageValue();
    return ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
  }

  private get isEn(): boolean {
    return this.languageService.getCurrentLanguageValue() === 'en';
  }

  rateLabel(rateType: string | null | undefined): string {
    switch (rateType) {
      case 'FTL': return this.t('ffSearch.rateFtl', 'Фура (FTL)');
      case 'LTL': return this.t('ffSearch.rateLtl', 'Сборный (LTL)');
      case 'AIR': return this.t('ffSearch.rateAir', 'Авиа');
      case 'CONTAINER': return this.t('ffSearch.rateContainer', 'Контейнер');
      default: return rateType || '';
    }
  }

  countryName(id: number | undefined): string {
    const c = this.countries.find(x => x.id === id);
    return c ? (this.isEn ? (c.nameEn || c.nameRu) : (c.nameRu || c.nameEn)) : '';
  }

  cargoTypeName(id: number): string {
    const c = this.cargoTypes.find(x => x.id === id);
    return c ? (this.isEn ? (c.nameEn || c.nameRu) : (c.nameRu || c.nameEn)) : '';
  }

  fmtWeight(kg: number | undefined): string {
    if (!kg) return '—';
    // Всегда в килограммах — экспедиторам так привычнее сравнивать
    return `${new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 1 }).format(kg)} ${this.t('ffSearch.kg', 'кг')}`;
  }

  fmtVolume(m3: number | undefined): string {
    if (!m3) return '—';
    return new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: m3 < 1 ? 3 : 1 }).format(m3) + ' м³';
  }

  productVolume(p: CargoRequestProduct): number {
    if (p.volumeM3) return p.volumeM3;
    if (p.lengthMm && p.widthMm && p.heightMm) return (p.lengthMm * p.widthMm * p.heightMm) / 1e9 * (p.quantity || 1);
    return 0;
  }

  get totalWeight(): number {
    return (this.request?.products ?? []).reduce((sum, p) => sum + (p.totalWeightKg || 0), 0);
  }

  get totalVolume(): number {
    return (this.request?.products ?? []).reduce((sum, p) => sum + this.productVolume(p), 0);
  }

  /** Особенности по всему запросу: флаг стоит хотя бы у одной позиции — показываем его, иначе — обратное. */
  get cargoTraits(): { label: string; on: boolean; warn: boolean }[] {
    const products = this.request?.products ?? [];
    const any = (key: 'isDangerous' | 'isOversized' | 'isNonStackable') => products.some(p => !!p[key]);
    return [
      any('isDangerous')
        ? { label: this.t('ffSearch.dangerous', 'Опасный груз'), on: true, warn: true }
        : { label: this.t('ffRequest.notDangerous', 'Неопасный груз'), on: false, warn: false },
      any('isOversized')
        ? { label: this.t('ffSearch.oversized', 'Крупногабаритный груз'), on: true, warn: true }
        : { label: this.t('ffRequest.notOversized', 'Стандартные габариты'), on: false, warn: false },
      any('isNonStackable')
        ? { label: this.t('ffSearch.nonStackable', 'Не штабелировать'), on: true, warn: false }
        : { label: this.t('ffRequest.stackable', 'Можно штабелировать'), on: false, warn: false },
    ];
  }

  get placesCount(): number {
    return (this.request?.products ?? []).reduce((sum, p) => sum + (p.quantity || 1), 0);
  }

  fmtMoney(amount: number | undefined, currencyCode: string | undefined): string {
    return `${new Intl.NumberFormat(this.numberLocale, { maximumFractionDigits: 0 }).format(Math.round(amount || 0))} ${currencyCode || ''}`.trim();
  }

  currencyCode(currencyId: number | undefined): string {
    return this.currencies.find(c => c.id === currencyId)?.code || '';
  }

  /** Состояние для шапки: перевозчик выбран (не мы) / наша ставка выиграла / ставка отправлена / можно ставить. */
  get pageStatus(): { text: string; tone: string } {
    if (this.myBid?.bidStatus === 'ACCEPTED') return { text: this.t('ffRequest.statusWon', 'Ваша ставка выбрана'), tone: 'green' };
    if (this.myBid?.bidStatus === 'LOST' || (this.request?.hasDeal && !this.myBid)) return { text: this.t('ffSearch.statusDeal', 'Перевозчик выбран'), tone: 'amber' };
    if (this.myBid) return { text: this.t('ffSearch.statusMine', 'Ваша ставка отправлена'), tone: 'blue' };
    return { text: this.t('ffSearch.statusOpen', 'Принимает ставки'), tone: 'open' };
  }

  bidTone(): string {
    if (this.myBid?.bidStatus === 'ACCEPTED') return 'green';
    if (this.myBid?.bidStatus === 'LOST') return 'red';
    return 'blue';
  }

  /** Главная кнопка: сделать ставку (если можно) или изменить свою. */
  get primaryAction(): 'make' | 'edit' | null {
    if (this.loadingBid || this.request?.hasDeal) return null;
    if (!this.myBid) return 'make';
    return this.canEditBid() ? 'edit' : null;
  }

  onPrimaryAction(): void {
    if (this.primaryAction === 'make') this.makeBid();
    else if (this.primaryAction === 'edit') this.editBid();
  }

  /** «Скопировать для мессенджеров»: вся информация по запросу простым текстом (без разметки — одинаково в WhatsApp и Telegram). */
  copied = false;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  async copyForMessenger(): Promise<void> {
    const text = this.messengerText();
    if (!text) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      // запасной путь для браузеров без Clipboard API
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      ta.remove();
    }
    if (!ok) return;
    this.copied = true;
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => { this.copied = false; this.cdr.markForCheck(); }, 2000);
    this.cdr.markForCheck();
  }

  private messengerText(): string {
    const r = this.request;
    if (!r) return '';
    const date = (v: string | undefined) => {
      if (!v) return '—';
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    };
    const place = (city: string | undefined, countryId: number | undefined) =>
      [city, this.countryName(countryId)].filter(Boolean).join(', ');

    const lines: string[] = [
      `${this.t('ffRequest.title', 'Запрос')} ${this.requestNo(r)} · ${this.rateLabel(r.rateType)}`,
      `${this.t('ffRequest.routeLabel', 'Маршрут')}: ${place(r.fromCity, r.fromCountryId)} → ${place(r.toCity, r.toCountryId)}`,
      `${this.t('freightForwarder.requests.loadingDate', 'Дата погрузки')}: ${date(r.loadingDate)}`,
      `${this.t('ffRequest.traits', 'Особенности')}: ${this.cargoTraits.map(f => f.label).join(', ')}`,
      '',
      `${this.t('ffRequest.totalTitle', 'Итого по всему грузу')}:`,
      `${this.t('ffRequest.totalWeight', 'Общий вес')}: ${this.fmtWeight(this.totalWeight)}`,
      `${this.t('ffRequest.totalVolume', 'Общий объём')}: ${this.fmtVolume(this.totalVolume)}`,
      `${this.t('ffRequest.totalPlaces', 'Всего мест')}: ${this.placesCount}`,
      `${this.t('freightForwarder.requests.price', 'Стоимость груза')}: ${this.fmtMoney(r.price, this.currencyCode(r.currencyId))}`,
    ];

    const products = r.products ?? [];
    if (products.length) {
      lines.push('', `${this.t('ffRequest.itemsTitle', 'Грузовые места')}:`);
      products.forEach((p, i) => {
        const name = p.name || this.cargoTypeName(p.cargoTypeId) || this.t('ffRequest.item', 'Груз');
        const parts = [
          `${this.t('ffRequest.itemPlaces', 'Количество мест')}: ${p.quantity || 1}`,
          `${this.t('ffSearch.weight', 'Вес')}: ${this.fmtWeight(p.totalWeightKg)}`,
        ];
        const vol = this.productVolume(p);
        if (vol) parts.push(`${this.t('ffSearch.volume', 'Объём')}: ${this.fmtVolume(vol)}`);
        if (p.lengthMm && p.widthMm && p.heightMm) parts.push(`${this.t('ffRequest.itemSize', 'Размер места, мм')}: ${p.lengthMm}×${p.widthMm}×${p.heightMm}`);
        if (p.tnvedCode) parts.push(`${this.t('ffRequest.tnved', 'Код ТНВЭД')}: ${p.tnvedCode}`);
        const flags = [
          p.isDangerous && this.t('ffSearch.dangerous', 'Опасный груз'),
          p.isOversized && this.t('ffSearch.oversized', 'Крупногабаритный груз'),
          p.isNonStackable && this.t('ffSearch.nonStackable', 'Не штабелировать'),
        ].filter(Boolean);
        if (flags.length) parts.push(flags.join(', '));
        lines.push(`${i + 1}. ${name}`, ...parts.map(x => `   ${x}`));
      });
    }
    return lines.join('\n');
  }

  /** Место цены после решения клиента: «Ваша цена была 2-й по выгодности из 5». */
  priceRankText(position: number): string {
    if (position === 1) return this.t('ffRequest.rankBest', 'Ваша цена была самой выгодной');
    const total = this.request?.bidsCount;
    return total && total >= position
      ? this.t('ffRequest.rankOf', 'Ваша цена была {pos}-й по выгодности из {n}').replace('{pos}', '' + position).replace('{n}', '' + total)
      : this.t('ffRequest.rank', 'Ваша цена была {pos}-й по выгодности').replace('{pos}', '' + position);
  }

  /** «Моя ставка» → «Подробнее»: примечание и дата изменения. */
  bidExpanded = false;

  hasBidDetails(b: FreightForwarderBidDto): boolean {
    return !!(b.note || (b.updatedAt && b.updatedAt !== b.createdAt));
  }

  get backLabel(): string {
    return this.returnTo === 'dashboard'
      ? this.t('freightForwarder.dashboard.bidsTab', 'Мои ставки')
      : this.t('header.searchOrders', 'Поиск запросов');
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('freightForwarder.viewRequest.title') || 'Просмотр запроса';
    this.backText = this.languageService.translate('freightForwarder.viewRequest.back') || 'Назад к списку';
    this.chatButtonText = this.languageService.translate('freightForwarder.viewRequest.chatButton') || 'Чат';
    this.myBidSectionTitleText = this.languageService.translate('freightForwarder.viewRequest.myBidSectionTitle') || 'Моя ставка';
    this.noBidText = this.languageService.translate('freightForwarder.viewRequest.noBid') || 'Вы еще не сделали ставку на этот запрос';
    this.makeBidText = this.languageService.translate('freightForwarder.viewRequest.makeBid') || 'Сделать ставку';
    this.editBidText = this.languageService.translate('freightForwarder.viewRequest.editBid') || 'Редактировать ставку';
    this.loadingBidText = this.languageService.translate('freightForwarder.viewRequest.loadingBid') || 'Загрузка ставки...';
    this.priceText = this.languageService.translate('freightForwarder.viewRequest.price') || 'Стоимость перевозки';
    this.currencyText = this.languageService.translate('freightForwarder.viewRequest.currency') || 'Валюта';
    this.deliveryDaysText = this.languageService.translate('freightForwarder.viewRequest.deliveryDays') || 'Срок доставки';
    this.servicesText = this.languageService.translate('freightForwarder.viewRequest.services') || 'Услуги';
    this.noteText = this.languageService.translate('freightForwarder.viewRequest.note') || 'Примечание';
    this.daysText = this.languageService.translate('freightForwarder.viewRequest.days') || 'дней';
    this.createdAtText = this.languageService.translate('freightForwarder.viewRequest.createdAt') || 'Дата создания';
    this.statusText = this.languageService.translate('freightForwarder.viewRequest.status') || 'Статус';
    this.acceptedText = this.languageService.translate('freightForwarder.viewRequest.accepted') || 'Принята';
    this.pendingText = this.languageService.translate('freightForwarder.viewRequest.pending') || 'Ожидает';
    this.bidAcceptedInfoText = this.languageService.translate('freightForwarder.viewRequest.bidAcceptedInfo') || 'Ставка принята. Редактирование и удаление невозможны.';
    this.bidLostInfoText = this.languageService.translate('freightForwarder.viewRequest.bidLostInfo') || 'Ставка проиграла. Редактирование и удаление невозможны.';
    this.deleteBidText = this.languageService.translate('freightForwarder.viewRequest.deleteBid') || 'Удалить ставку';
    this.deletingBidText = this.languageService.translate('freightForwarder.viewRequest.deletingBid') || 'Удаление...';
    this.deleteBidConfirmText = this.languageService.translate('freightForwarder.viewRequest.deleteBidConfirm') || 'Вы уверены, что хотите удалить эту ставку?';
    this.deleteBidSuccessText = this.languageService.translate('freightForwarder.viewRequest.deleteBidSuccess') || 'Ставка успешно удалена';
    this.cdr.markForCheck();
  }

  private loadMyBid(cargoRequestId: number): void {
    this.loadingBid = true;
    this.errorBid = null;

    // Загружаем ставки экспедитора с фильтром по cargoRequestId
    this.subscriptions.add(
      this.bidService.getAllBidsPaginated(0, 10, { cargoRequestId }).subscribe({
        next: (response) => {
          // Должна быть только одна ставка экспедитора на один запрос
          this.myBid = response.content.length > 0 ? response.content[0] : null;
          this.loadingBid = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('FreightForwarderViewRequest: Error loading bid', err);
          this.errorBid = 'Ошибка при загрузке ставки';
          this.loadingBid = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  makeBid(): void {
    if (this.requestId) {
      // Передаем параметр returnTo, чтобы после создания ставки вернуться на правильную страницу
      const queryParams = this.returnTo ? { returnTo: this.returnTo } : {};
      this.router.navigate(['/freight-forwarder/create-bid', this.requestId], { queryParams });
    }
  }

  editBid(): void {
    if (this.myBid?.id) {
      this.router.navigate(['/freight-forwarder/edit-bid', this.myBid.id]);
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getServiceNames(bid: FreightForwarderBidDto): string {
    if (!bid.services || bid.services.length === 0) return '-';
    const lang = this.languageService.getCurrentLanguageValue();
    return bid.services.map(service => lang === 'en' ? service.nameEn : service.nameRu).join(', ');
  }

  getBidStatusText(): string {
    if (!this.myBid) return '';
    
    if (this.myBid.bidStatus === 'ACCEPTED') {
      return this.acceptedText;
    } else if (this.myBid.bidStatus === 'LOST') {
      return this.t('ffDash.statusLost', 'Не выбрана');
    }
    return this.t('ffSearch.statusMine', 'Ваша ставка отправлена');
  }

  canEditBid(): boolean {
    if (!this.myBid) return false;
    // Нельзя редактировать, если статус ACCEPTED или LOST
    return this.myBid.bidStatus !== 'ACCEPTED' && this.myBid.bidStatus !== 'LOST';
  }

  getBidInfoText(): string {
    if (!this.myBid) return '';
    if (this.myBid.bidStatus === 'ACCEPTED') {
      return this.bidAcceptedInfoText;
    } else if (this.myBid.bidStatus === 'LOST') {
      return this.bidLostInfoText;
    }
    return '';
  }

  goBack(): void {
    // Возвращаемся туда, откуда пришли
    if (this.returnTo === 'dashboard') {
      this.router.navigate(['/freight-forwarder/dashboard/bids']);
    } else {
      // По умолчанию возвращаемся на список запросов
      this.router.navigate(['/freight-forwarder/requests']);
    }
  }

  async deleteBid(): Promise<void> {
    if (!this.myBid?.id || !this.canEditBid() || !this.requestId) return;

    if (!await this.confirmDialog.confirm(this.deleteBidConfirmText)) {
      return;
    }

    this.deletingBid = true;
    this.errorBid = null;

    this.subscriptions.add(
      this.bidService.deleteBid(this.myBid.id).subscribe({
        next: () => {
          this.deletingBid = false;
          // После удаления перезагружаем ставку (она должна исчезнуть, так как помечена как deleted)
          this.loadMyBid(this.requestId!);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error deleting bid', err);
          this.errorBid = err.error?.message || 'Ошибка при удалении ставки';
          this.deletingBid = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  /** Администратор назначил (или снял) менеджера, который ведёт запрос. */
  onForwarderManagerAssigned(change: AssigneeChange): void {
    if (this.request) {
      this.request = { ...this.request, forwarderManagerId: change.id ?? undefined, forwarderManagerName: change.name ?? undefined };
    }
  }
}
